import type { AboutCard as AboutCardType } from "@/__generated__/api/wrenAPI.schemas";

function Avatar({ name, index }: { name: string; index: number }) {
  const initial = name[0]?.toUpperCase() || "?";
  const bg = index === 0 ? "bg-[#c27a5a]" : "bg-[#638308]";

  return (
    <div
      className={`w-9 h-9 rounded-full ${bg} flex items-center justify-center text-white text-sm font-semibold flex-shrink-0`}
    >
      {initial}
    </div>
  );
}

export function AboutCard({ data }: { data: AboutCardType }) {
  return (
    <div>
      {/* Section header */}
      <h3 className="text-xs font-semibold text-[#638308] uppercase tracking-wider px-1 mb-4">
        What this is really about
      </h3>

      {/* Single unified card: white top → beige middle → dark bottom */}
      <div className="rounded-2xl shadow-[0_1px_10px_rgba(0,0,0,0.08)] overflow-hidden">
        {/* White section: On the Surface */}
        <div className="bg-white relative pb-4">
          <div className="px-6 pt-6 pb-2">
            <p className="text-xs font-semibold text-[#999] uppercase tracking-wider mb-4">
              On the surface
            </p>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#E8EDD8] flex items-center justify-center flex-shrink-0">
                {data.surface_emoji ? (
                  <span className="text-xl">{data.surface_emoji}</span>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#638308" strokeWidth="2">
                    <path d="M21 15C21 15.55 20.55 16 20 16H4C3.45 16 3 15.55 3 15V4C3 3.45 3.45 3 4 3H20C20.55 3 21 3.45 21 4V15Z" />
                    <path d="M7 21L12 16L17 21" />
                  </svg>
                )}
              </div>
              <p className="text-lg font-semibold text-[#1a1a1a]">{data.surface_topic}</p>
            </div>
          </div>

          {/* BUT REALLY pill — overlaps boundary between white and beige */}
          <div className="absolute -bottom-3.5 left-0 right-0 flex justify-center z-10">
            <span className="text-xs font-bold text-white bg-[#4a5d23] px-6 py-2 rounded-full uppercase tracking-wider shadow-sm">
              But really
            </span>
          </div>
        </div>

        {/* Warm beige section: What's Actually Felt */}
        <div className="bg-[#f5f3ef] px-6 pt-8 pb-6">
          <p className="text-xs font-semibold text-[#999] uppercase tracking-wider mb-4">
            What&apos;s actually felt
          </p>
          <div className="space-y-4">
            {data.people.map((person, i) => (
              <div key={i}>
                <div className="flex items-center gap-2 mb-2">
                  <Avatar name={person.name} index={i} />
                  <span className="text-base font-semibold text-[#1a1a1a]">{person.name}</span>
                </div>
                <div className="flex flex-wrap gap-2 pl-12">
                  {person.feelings.map((feeling, fi) => (
                    <span
                      key={fi}
                      className="text-xs px-2 py-1 rounded-full border border-[#d4d4d0] text-[#555] font-medium"
                    >
                      {feeling}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dark section: What's Really Being Asked */}
        <div className="bg-[#2d2d2d] px-6 pt-6 pb-6">
          <p className="text-xs font-semibold text-[#999] uppercase tracking-wider mb-4">
            What&apos;s really being asked
          </p>
          <div className="space-y-6">
            {data.people.map((person, i) => (
              <div key={i} className="flex items-start gap-4">
                <Avatar name={person.name} index={i} />
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold text-white mb-1">
                    &ldquo;{person.real_question}&rdquo;
                  </p>
                  <p className="text-sm text-[#aaa] leading-relaxed">
                    {person.real_question_explanation}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
